// frontend/src/components/FollowersModal.js
// Usage in Profile or ChannelDetail:
//   <FollowersModal
//     isOpen={showFollowers}
//     onClose={() => setShowFollowers(false)}
//     userId={targetUserId}
//     mode="followers"   // or "following"
//   />

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import API_BASE_URL from "../config";
import { X, Users, UserCheck, MessageCircle, Loader2, Search } from "lucide-react";

export default function FollowersModal({ isOpen, onClose, userId, mode = "followers" }) {
  const { user: me } = useAuth();
  const navigate = useNavigate();

  const [list, setList] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [followingMap, setFollowingMap] = useState({}); // userId → bool

  useEffect(() => {
    if (!isOpen || !userId) return;
    setSearch("");
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId, mode]);

  useEffect(() => {
    if (!search.trim()) {
      setFiltered(list);
    } else {
      setFiltered(
        list.filter((u) =>
          u.username.toLowerCase().includes(search.toLowerCase())
        )
      );
    }
  }, [search, list]);

  const fetchList = async () => {
    setLoading(true);
    try {
      const endpoint =
        mode === "followers"
          ? `${API_BASE_URL}/api/users/${userId}/followers`
          : `${API_BASE_URL}/api/users/${userId}/following`;

      const res = await axios.get(endpoint, { withCredentials: true });
      setList(res.data);
      setFiltered(res.data);

      // Build a map of who the logged-in user already follows
      const map = {};
      res.data.forEach((u) => {
        map[u.id] = u.is_followed_by_me ?? false;
      });
      setFollowingMap(map);
    } catch (err) {
      console.error("Error fetching list", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleFollow = async (targetId) => {
    const currently = followingMap[targetId];
    setFollowingMap((prev) => ({ ...prev, [targetId]: !currently }));
    try {
      if (currently) {
        await axios.delete(`${API_BASE_URL}/api/users/${targetId}/follow`, {
          withCredentials: true,
        });
      } else {
        await axios.post(`${API_BASE_URL}/api/users/${targetId}/follow`, {}, {
          withCredentials: true,
        });
      }
    } catch (err) {
      console.error("Toggle follow error", err);
      // Revert on failure
      setFollowingMap((prev) => ({ ...prev, [targetId]: currently }));
    }
  };

  const goToProfile = (uid) => {
    onClose();
    navigate(`/profile/${uid}`);
  };

  const openDM = (uid) => {
    onClose();
    navigate(`/messages?with=${uid}`);
  };

  const getAvatar = (username, avatar) =>
    avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${username}`;

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .fm-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,.45);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          animation: fm-fade-in .2s ease;
        }
        @keyframes fm-fade-in { from { opacity:0 } to { opacity:1 } }

        .fm-modal {
          background: #fff;
          border-radius: 16px;
          width: 100%;
          max-width: 420px;
          max-height: 80vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0,0,0,.2);
          animation: fm-slide-up .25s ease;
          overflow: hidden;
        }
        @keyframes fm-slide-up {
          from { transform: translateY(24px); opacity:0; }
          to   { transform: translateY(0);    opacity:1; }
        }

        .fm-header {
          padding: 20px 20px 14px;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
        }
        .fm-title {
          font-size: 17px;
          font-weight: 700;
          color: #1e293b;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .fm-close {
          background: none;
          border: none;
          cursor: pointer;
          color: #64748b;
          padding: 6px;
          border-radius: 8px;
          display: flex;
          transition: background .15s;
        }
        .fm-close:hover { background: #f1f5f9; }

        .fm-search-wrap {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          position: relative;
        }
        .fm-search-icon {
          position: absolute;
          left: 28px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }
        .fm-search {
          width: 100%;
          padding: 9px 12px 9px 34px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 14px;
          outline: none;
          background: #f8fafc;
          box-sizing: border-box;
        }
        .fm-search:focus { border-color: #6366f1; background: #fff; }

        .fm-list {
          flex: 1;
          overflow-y: auto;
          padding: 8px 0;
        }
        .fm-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px;
          color: #94a3b8;
          gap: 8px;
          font-size: 14px;
        }
        .fm-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          color: #94a3b8;
          gap: 10px;
          text-align: center;
          font-size: 14px;
        }
        .fm-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 16px;
          transition: background .15s;
        }
        .fm-item:hover { background: #f8fafc; }
        .fm-avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid #e2e8f0;
          flex-shrink: 0;
          cursor: pointer;
        }
        .fm-info {
          flex: 1;
          min-width: 0;
        }
        .fm-username {
          font-weight: 600;
          font-size: 14px;
          color: #1e293b;
          cursor: pointer;
          background: none;
          border: none;
          padding: 0;
          text-align: left;
        }
        .fm-username:hover { text-decoration: underline; }
        .fm-bio {
          font-size: 12px;
          color: #64748b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin: 2px 0 0;
        }
        .fm-actions {
          display: flex;
          gap: 6px;
          flex-shrink: 0;
        }
        .fm-btn {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          transition: background .15s, color .15s;
        }
        .fm-btn-follow {
          background: #6366f1;
          color: #fff;
        }
        .fm-btn-follow:hover { background: #4f46e5; }
        .fm-btn-unfollow {
          background: #f1f5f9;
          color: #1e293b;
          border: 1px solid #e2e8f0;
        }
        .fm-btn-unfollow:hover { background: #fee2e2; color: #dc2626; border-color: #fca5a5; }
        .fm-btn-dm {
          background: #f1f5f9;
          color: #6366f1;
          padding: 6px 8px;
        }
        .fm-btn-dm:hover { background: #eef2ff; }

        .fm-badge-followed {
          font-size: 11px;
          color: #6366f1;
          background: #eef2ff;
          padding: 2px 8px;
          border-radius: 20px;
          font-weight: 600;
        }

        @media (max-width: 480px) {
          .fm-modal { max-height: 90vh; border-radius: 16px 16px 0 0; margin-top: auto; }
          .fm-overlay { align-items: flex-end; padding: 0; }
        }
      `}</style>

      <div className="fm-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="fm-modal">
          {/* Header */}
          <div className="fm-header">
            <div className="fm-title">
              {mode === "followers" ? <Users size={20} /> : <UserCheck size={20} />}
              {mode === "followers" ? "Followers" : "Following"}
              {list.length > 0 && (
                <span style={{ fontSize: 14, fontWeight: 500, color: "#64748b" }}>
                  ({list.length})
                </span>
              )}
            </div>
            <button className="fm-close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>

          {/* Search */}
          {list.length > 5 && (
            <div className="fm-search-wrap">
              <Search size={15} className="fm-search-icon" />
              <input
                className="fm-search"
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
          )}

          {/* List */}
          <div className="fm-list">
            {loading && (
              <div className="fm-loading">
                <Loader2 size={20} className="spin" />
                Loading...
              </div>
            )}

            {!loading && filtered.length === 0 && (
              <div className="fm-empty">
                <Users size={36} opacity={0.3} />
                <p>
                  {search
                    ? "No results match your search"
                    : mode === "followers"
                    ? "No followers yet"
                    : "Not following anyone yet"}
                </p>
              </div>
            )}

            {!loading &&
              filtered.map((u) => {
                const isMe = u.id === me?.id;
                const isFollowing = followingMap[u.id];

                return (
                  <div key={u.id} className="fm-item">
                    <img
                      src={getAvatar(u.username, u.avatar)}
                      alt={u.username}
                      className="fm-avatar"
                      onClick={() => goToProfile(u.id)}
                    />
                    <div className="fm-info">
                      <button className="fm-username" onClick={() => goToProfile(u.id)}>
                        {u.username}
                      </button>
                      {u.bio && <p className="fm-bio">{u.bio}</p>}
                      {u.follows_you && !isMe && (
                        <span className="fm-badge-followed">Follows you</span>
                      )}
                    </div>
                    {!isMe && (
                      <div className="fm-actions">
                        {/* DM button */}
                        <button
                          className="fm-btn fm-btn-dm"
                          onClick={() => openDM(u.id)}
                          title="Send message"
                        >
                          <MessageCircle size={15} />
                        </button>

                        {/* Follow/Unfollow */}
                        <button
                          className={`fm-btn ${isFollowing ? "fm-btn-unfollow" : "fm-btn-follow"}`}
                          onClick={() => toggleFollow(u.id)}
                        >
                          {isFollowing ? "Following" : "Follow"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}